import { useState } from 'react';
import { endetBald, fristKurz, SICHTBARKEIT_TEXT, type PollView } from '../../shared/polls';
import { pollsApi } from '../lib/api';
import { Markdown } from '../lib/Markdown';

/**
 * Abfrage als Karte im Schwarzen Brett (Design-System §6 „Abfrage“).
 *
 * Was jemand sehen darf, entscheidet der Server: `results` ist null, wenn die
 * Zahlen fuer diese Person nicht freigegeben sind. Namen kommen hier nie an.
 */
export function PollCard({
  poll,
  onAnswered,
  onError,
}: {
  poll: PollView;
  onAnswered: (text: string) => Promise<void> | void;
  onError: (text: string) => void;
}) {
  const [auswahl, setAuswahl] = useState<string[]>(poll.my_option_ids);
  const [sendet, setSendet] = useState(false);

  // Anonym beantwortet: der Hub weiss nicht, was gewaehlt wurde – nicht aenderbar.
  const gesperrt = !poll.open || (poll.anonymous && poll.answered);
  const geaendert =
    auswahl.length !== poll.my_option_ids.length ||
    auswahl.some((id) => !poll.my_option_ids.includes(id));
  const bald = poll.open && endetBald(poll.closes_at);

  function waehle(id: string) {
    if (gesperrt) return;
    setAuswahl((jetzt) =>
      poll.multiple
        ? jetzt.includes(id)
          ? jetzt.filter((x) => x !== id)
          : [...jetzt, id]
        : [id],
    );
  }

  async function senden() {
    setSendet(true);
    try {
      await pollsApi.answer(poll.id, auswahl);
      await onAnswered(poll.answered ? 'Antwort geändert.' : 'Antwort gesendet.');
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSendet(false);
    }
  }

  const meine = poll.options
    .filter((o) => poll.my_option_ids.includes(o.id))
    .map((o) => o.label.toLowerCase())
    .join(', ');

  const summe = poll.results?.reduce((n, r) => n + r.count, 0) ?? 0;
  // Bei Mehrfachauswahl ist der Anteil je Option bezogen auf die Teilnehmenden.
  const basis = Math.max(1, poll.multiple ? (poll.participants ?? summe) : summe);

  return (
    <article className="note">
      <div className="note__head">
        <h3 className="note__title">{poll.title}</h3>
        <span className="note__pills">
          <span className="badge-pill">abfrage</span>
          {poll.anonymous && <span className="badge-pill badge-pill--muted">anonym</span>}
          {!poll.open && <span className="badge-pill badge-pill--muted">beendet</span>}
        </span>
      </div>
      <p className={`note__meta${bald ? ' note__meta--bald' : ''}`}>
        {poll.open
          ? `läuft bis ${fristKurz(poll.closes_at)}${bald ? ' · endet bald' : ''}`
          : `beendet am ${fristKurz(poll.closes_at)}${
              poll.participants !== null ? ` · ${poll.participants} antworten` : ''
            }`}
      </p>
      {poll.body_md && (
        <div className="note__body">
          <Markdown source={poll.body_md} />
        </div>
      )}

      {poll.results ? (
        <div className="erg" aria-label="Ergebnis">
          {poll.options.map((o) => {
            const n = poll.results?.find((r) => r.option_id === o.id)?.count ?? 0;
            return (
              <div className="erg__row" key={o.id}>
                <span>{o.label}</span>
                <span className="erg__n">{n}</span>
                <div className="erg__bar" aria-hidden="true">
                  <div className="erg__fill" style={{ width: `${Math.round((n / basis) * 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {poll.open && (
        <div
          className="poll"
          role={poll.multiple ? 'group' : 'radiogroup'}
          aria-label={`Antwort: ${poll.title}`}
        >
          {poll.options.map((o) => {
            const an = !gesperrt && auswahl.includes(o.id);
            return (
              <button
                key={o.id}
                type="button"
                className="poll__opt"
                role={poll.multiple ? 'checkbox' : 'radio'}
                aria-checked={an}
                disabled={gesperrt || sendet}
                onClick={() => waehle(o.id)}
              >
                <span className="poll__mark" aria-hidden="true">
                  {an ? (poll.multiple ? '✓' : '●') : ''}
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="poll__foot">
        {poll.open && !gesperrt && (
          <button
            type="button"
            className="px-btn px-btn--sm"
            disabled={sendet || auswahl.length === 0 || (poll.answered && !geaendert)}
            onClick={() => void senden()}
          >
            {sendet ? 'Sendet …' : poll.answered ? 'Antwort ändern' : 'Antwort senden'}
          </button>
        )}
        <span className="poll__hint">
          {poll.open
            ? poll.answered
              ? poll.anonymous
                ? '> abgestimmt · anonym, nicht änderbar'
                : `> deine antwort: ${meine} · änderbar bis frist`
              : poll.anonymous
                ? '> anonym: die antwort lässt sich danach nicht ändern'
                : `> ${poll.mine ? 'von dir angelegt · ' : ''}${SICHTBARKEIT_TEXT[poll.results_visibility]}`
            : poll.results
              ? ''
              : '> ergebnis sieht nur die anlegende person'}
        </span>
      </div>
    </article>
  );
}
